import { toast } from "react-toastify";
// EditHistoryModal - りれきの かくにん（ピースビルダーの記録。決まりは docs/edit-history-undo.md）
// じゆうにつくると同じ形：区切り（開いた・閉じた・テストした・AIでつくった・10 分ごと・完成・公開・restore など）を
// 新しい順にカードで並べ、その下に「その区切りまでに やったこと」を並べる。最後の区切りのあとに やったことは、
// 一番上の「いま」の下に並べる。「もどす」（restore）は区切りのカードから。restore は 1 つの操作として undo で取り消せる。
// 見られる件数はプランで決まる（未課金は新しい 10 区切り分）。
// 高さは中身に関係なく固定（リスト・件数の注記はいつも同じ場所・同じ高さを占める）。
import { useRef, useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import dayjs from "dayjs";
import { AppDispatch, RootState } from "app/store";
import I18n from "common/i18n";
import Constants from "common/constant";
import { usePlaySound, btnDelay } from "common/utils";
import { actions } from "features/builder/slice";
import { getEditHistory } from "features/builder/api";
import { HELP_HISTORY } from "features/builder/helpPages";
import { useBuilderContext } from "features/builder/context";
import { PieceMilestone, PieceEditHistoryEntry } from "features/builder/types";
import { savePieceMeta, recordMilestoneWithCapture } from "features/builder/hook";
import { renderMapModelsPreview } from "features/builder/renderGeneratedMapPreview";
import {
  PillSize,
  PillColor,
  InScreenModal,
  BuilderPillButton,
  BuilderModalPanel,
  BuilderModalAccent,
  BuilderConfirmDialog,
} from "features/builder/elements";
import { BuilderHelpPopup } from "./BuilderHelpPopup";
import { resolveHistoryLabel, isMilestoneAction, resolveMilestone } from "./editHistoryLabels";

/** 区切りのカードと、その下に並べる やったこと（その区切りまでに やったこと、新しい順） */
type HistoryGroup = { milestone: PieceEditHistoryEntry | null; edits: PieceEditHistoryEntry[] };

/** seq の古い順の記録を、区切りごとにまとめて新しい順に返す。先頭は「いま」（最後の区切りのあとの分） */
const groupHistory = (entries: PieceEditHistoryEntry[]): HistoryGroup[] => {
  const groups: HistoryGroup[] = [{ milestone: null, edits: [] }];
  // 新しい方から見ていき、区切りに出会ったら、それより古い やったことをその区切りに入れる
  [...entries].reverse().forEach((entry) => {
    if (isMilestoneAction(entry.action_type)) {
      groups.push({ milestone: entry, edits: [] });
    } else {
      groups[groups.length - 1].edits.push(entry);
    }
  });
  return groups;
};

const DEFAULT_CAPTURE = `${Constants.assetHost}/assets/images/default_capture.png`;

/** キャプチャを持たない昔の区切りのカードに出す、マップを描いた画像（ピース・seq ごと） */
const previewCache = new Map<string, string>();

/** 区切りのカードの画像。キャプチャが無い昔の区切りは、その行のマップを描いて出す */
const MilestoneThumbnail = ({ entry }: { entry: PieceEditHistoryEntry }) => {
  const { scene } = useBuilderContext();
  const pieceId = useSelector((state: RootState) => state.builder.editingBuilderMapId);
  const key = `${pieceId}_${entry.seq}`;
  const [preview, setPreview] = useState<string | null>(previewCache.get(key) ?? null);

  useEffect(() => {
    if (entry.capture || preview || !scene || !entry.map || !entry.size) return;
    let canceled = false;
    renderMapModelsPreview(scene, entry.map, { x: entry.size.x, z: entry.size.z })
      .then((url) => {
        previewCache.set(key, url);
        if (!canceled) setPreview(url);
      })
      .catch(() => undefined);
    return () => {
      canceled = true;
    };
  }, [entry, preview, scene, key]);

  return (
    <img
      src={entry.capture || preview || DEFAULT_CAPTURE}
      onError={(e) => {
        (e.target as HTMLImageElement).src = DEFAULT_CAPTURE;
      }}
      // マップの背景と カードの白を見分けられるように枠をつける
      className="w-24 h-16 shrink-0 object-cover rounded-lg bg-beige-60 border-solid border-2 border-gray2-60 box-border"
    />
  );
};

/** 区切りの時刻（カード・戻す確認・戻したトーストで同じ書き方にする） */
const milestoneTime = (entry: PieceEditHistoryEntry) => dayjs(entry.created_at).format("M/D HH:mm");

export const EditHistoryModal = ({ open, onClose }: { open: boolean; onClose: () => void }) => {
  const play = usePlaySound();
  const dispatch = useDispatch<AppDispatch>();
  const editHistory = useSelector((state: RootState) => state.builder.editHistory);
  const pieceId = useSelector((state: RootState) => state.builder.editingBuilderMapId);
  const { scene, triggerReload } = useBuilderContext();
  // 「もどす」確認中の区切り（null = 確認ダイアログ非表示）
  const [restoreTarget, setRestoreTarget] = useState<PieceEditHistoryEntry | null>(null);
  const restoring = useRef(false);
  const [showHelp, setShowHelp] = useState(false);
  // 見られる区切りの数（-1 は全部）と、区切りの総数。超えたら注記を出す
  const [visible, setVisible] = useState<{ limit: number; total: number } | null>(null);

  // 開いたときにサーバ権威の履歴へ同期（seq を確定し、復元の整合性を担保）
  useEffect(() => {
    if (!open || !pieceId || pieceId === "local-map") return;
    getEditHistory({ piece_id: pieceId })
      .then((history) => {
        dispatch(actions.setEditHistory(history.entries));
        setVisible({ limit: history.limit, total: history.total_milestones });
      })
      .catch(() => undefined);
  }, [open, pieceId, dispatch]);

  const handleClose = () => {
    play();
    btnDelay(() => onClose());
  };

  const groups = groupHistory(editHistory);
  const limited = !!visible && visible.limit >= 0 && visible.total > visible.limit;

  const handleRestoreClick = (entry: PieceEditHistoryEntry) => {
    play();
    btnDelay(() => setRestoreTarget(entry));
  };

  const handleRestoreConfirm = async () => {
    const target = restoreTarget;
    setRestoreTarget(null);
    if (!target || restoring.current) return;
    if (!target.map) {
      toast.warn(
        <div>
          <p className="type-text type-sm text-textcolor-black">
            {I18n.t("MSG_BUILDER_ACTION_FAILED")}
          </p>
        </div>,
      );
      return;
    }
    restoring.current = true;
    // もどす前の中身も あとで戻せるように、いまの画面を撮って区切りを残す（前の区切りと同じなら残さない）
    await dispatch(recordMilestoneWithCapture(scene, PieceMilestone.BEFORE_RESTORE));
    // 区切りの中身をエディタへ適用する。後ろの記録は消さず、restore も区切りとして記録に残す。
    // restore は undo で取り消せる。区切りの画面は 3D を作り直してから撮る
    dispatch(actions.restoreToHistoryEntry({ seq: target.seq }));
    if (target.meta) dispatch(savePieceMeta(target.meta)).catch(() => undefined);
    dispatch(
      actions.requestMilestone({
        action_type: PieceMilestone.RESTORE,
        payload: { seq: target.seq },
      }),
    );
    triggerReload();
    restoring.current = false;
    toast.success(
      <div>
        <p className="type-text type-sm text-textcolor-black">
          {I18n.t("MSG_BUILDER_TOAST_RESTORED", { time: milestoneTime(target) })}
        </p>
      </div>,
    );
    onClose();
  };

  return (
    <>
      <InScreenModal open={open} onBackdropClose={handleClose}>
        <BuilderModalPanel
          accent={BuilderModalAccent.BLUE}
          icon="history"
          title={I18n.t("MSG_BUILDER_EDIT_HISTORY")}
          width={720}
          onClose={handleClose}
          onHelp={() => setShowHelp(true)}
        >
          {/* リストは高さ固定：件数・空に関わらず常に h-[440px] */}
          <div className="flex-col-el items-stretch w-full h-[440px] p-3 rounded-xl bg-beige-60 box-border">
            {editHistory.length === 0 ? (
              <div className="flex-col-el flex-center w-full h-full">
                <span className="type-text type-body text-textcolor-black/40">
                  {I18n.t("MSG_BUILDER_EDIT_HISTORY_EMPTY")}
                </span>
              </div>
            ) : (
              <div
                data-guide="history_list"
                className="flex-col-el items-stretch w-full h-full gap-2 pr-1 pb-1 box-border overflow-y-auto invisible-scrollbar"
              >
                {groups.map((group, index) => {
                  const milestone = group.milestone;
                  // 「いま」は最後の区切りのあとに やったことがあるときだけ出す
                  if (!milestone && group.edits.length === 0) return null;
                  // 一番新しい区切りのあとに何もしていなければ、その区切りがいまの中身（もどす は出さない）
                  const isCurrent = !!milestone && index === 1 && groups[0].edits.length === 0;
                  const card = milestone ? resolveMilestone(milestone) : null;
                  return (
                    <div
                      key={milestone?.seq ?? "now"}
                      className="flex-col-el items-stretch w-full gap-1"
                    >
                      {milestone && card ? (
                        <div
                          data-guide="history_card"
                          className="flex-row-el items-center w-full gap-3 bg-white rounded-xl p-3 box-border border-solid border-2 border-beige-alt drop-shadow-normal-show"
                        >
                          <MilestoneThumbnail entry={milestone} />
                          <div className="flex-col-el items-start flex-1 min-w-0 gap-1">
                            <div className="flex-row-el items-center gap-2 w-full">
                              <span
                                className={`flex-col-center shrink-0 w-8 h-8 rounded-lg ${card.badge}`}
                              >
                                <span className="material-symbols-outlined text-white type-sm">
                                  {card.icon}
                                </span>
                              </span>
                              {/* type-text-left の flex-shrink: 0 と white-space: pre-line を上書きしないと縮まない */}
                              <span className="type-text-left type-body text-textcolor-black truncate min-w-0 !shrink !whitespace-nowrap">
                                {card.label}
                              </span>
                            </div>
                            <div className="flex-row-el items-center gap-2">
                              {milestone.action_type === PieceMilestone.COMPLETE && (
                                <span className="flex-col-center shrink-0 rounded-full bg-blue-10 px-2 type-sm text-blue-100 whitespace-nowrap">
                                  {`V${milestone.version}`}
                                </span>
                              )}
                              <span className="type-text-left type-sm text-textcolor-black/50 whitespace-nowrap">
                                {milestoneTime(milestone)}
                              </span>
                            </div>
                          </div>
                          {isCurrent ? (
                            <span className="flex-col-center shrink-0 rounded-full bg-green-100 px-3 py-1 type-sm text-white whitespace-nowrap">
                              {I18n.t("MSG_BUILDER_EDIT_HISTORY_NOW")}
                            </span>
                          ) : (
                            milestone.map && (
                              <div data-guide="history_restore" className="shrink-0 pb-1">
                                <BuilderPillButton
                                  color={PillColor.BLUE}
                                  size={PillSize.SM}
                                  icon="undo"
                                  label={I18n.t("MSG_BUILDER_EDIT_HISTORY_RESTORE")}
                                  onClick={() => handleRestoreClick(milestone)}
                                />
                              </div>
                            )
                          )}
                        </div>
                      ) : (
                        // 最後の区切りのあとに やったこと（いまの中身）
                        <div className="flex-row-el items-center w-full gap-2 py-1">
                          <span className="flex-col-center shrink-0 rounded-full bg-green-100 px-3 py-1 type-sm text-white whitespace-nowrap">
                            {I18n.t("MSG_BUILDER_EDIT_HISTORY_NOW")}
                          </span>
                          <div className="flex-1 h-1 rounded-full bg-beige-alt" />
                        </div>
                      )}
                      {/* その区切りまでに やったこと。区切りのカードと見分けられるよう、線で下げて小さく並べる */}
                      {group.edits.length > 0 && (
                        <div className="flex-col-el items-stretch gap-1 ml-6 pl-3 border-solid border-0 border-l-4 border-beige-alt box-border">
                          {group.edits.map((entry) => {
                            const { label, icon } = resolveHistoryLabel(entry);
                            return (
                              <div
                                key={entry.seq}
                                className="flex-row-el items-center w-full gap-2"
                              >
                                <div className="flex-col-center shrink-0 w-8 h-8 rounded-lg bg-blue-100">
                                  <span className="material-symbols-outlined text-white type-sm">
                                    {icon}
                                  </span>
                                </div>
                                <span className="type-text-left type-sm text-textcolor-black flex-1 min-w-0 truncate">
                                  {label}
                                </span>
                                <span className="type-text-left type-sm text-textcolor-black/50 whitespace-nowrap">
                                  {dayjs(entry.created_at).format("HH:mm")}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          {/* 見られる件数の注記。高さは出ないときも同じ（周りを動かさない） */}
          <div className="flex-col-el flex-center w-full h-8 px-4">
            {limited && (
              <span className="type-text type-sm text-textcolor-black/50">
                {I18n.t("MSG_BUILDER_EDIT_HISTORY_LIMIT_NOTE", {
                  limit: visible.limit,
                  total: visible.total,
                })}
              </span>
            )}
          </div>
        </BuilderModalPanel>
      </InScreenModal>

      {/* 戻す確認（誤タップ防止）。どの時刻の区切りに戻すかを出す（トーストも同じ時刻を出す） */}
      <BuilderConfirmDialog
        visible={!!restoreTarget}
        accent={BuilderModalAccent.ORANGE}
        icon="history"
        confirmIcon="undo"
        confirmLabel={I18n.t("MSG_BUILDER_EDIT_HISTORY_RESTORE")}
        confirmSound={`${Constants.assetHost}/assets/sounds/switch-on.mp3`}
        title={I18n.t("MSG_BUILDER_EDIT_HISTORY_RESTORE_CONFIRM_TITLE")}
        message={I18n.t("MSG_BUILDER_EDIT_HISTORY_RESTORE_CONFIRM_MSG", {
          time: restoreTarget ? milestoneTime(restoreTarget) : "",
        })}
        close={() => setRestoreTarget(null)}
        handleConfirm={handleRestoreConfirm}
      />

      <BuilderHelpPopup
        open={showHelp}
        onClose={() => setShowHelp(false)}
        chapters={HELP_HISTORY}
      />
    </>
  );
};
